// The card's aggregator: the opening positions the reader went wrong in more
// than once. Each scanned game's opening slips (`GameFacts.openingSlips`,
// a move that gave up at least `OPENING_SLIP_PAWNS` against the engine's)
// are grouped by position (the FEN without its move counters), counting the
// games, not the moves; a position slipped in twice or more is a habit. The
// worst first: most games, then the most pawns given up on average.
// Registered with the game store by this plugin's setup; the store runs it
// over the last 30 days.
import { positionKey } from '@chess/api-insights';
import { aggregatorId, type Accumulator, type Aggregator, type Color, type Fen, type San, type Uci } from '@chess/api-model';

/** At most this many positions are shown. */
export const TOP_HABITS = 5;
/** A position slipped in this many games or more is a habit. */
export const HABIT_GAMES = 2;

/** One game's slip in a habit's position. */
export interface HabitGame {
  id: string;
  playedAt: number;
  /** The slip's 1-based ply. */
  ply: number;
  san: San;
  /** Pawns given up against the engine's move. */
  loss: number;
  reviewPath?: string;
}

/** A position the reader went wrong in, in more than one game. */
export interface OpeningHabit {
  /** The position before the move, the reader to move. */
  fen: Fen;
  colour: Color;
  /** The opening's variation, or its family, as the latest game names it. */
  opening?: string;
  /** The moves to the position, from the latest game. */
  line: San[];
  /** The engine's move there. */
  best: Uci;
  /** The move played most often there, and how many games it was. */
  played: San;
  playedGames: number;
  /** Every game with a slip there, newest first. */
  games: HabitGame[];
  /** Pawns given up there, on average over the games. */
  loss: number;
}

/** What the card shows: the habits, and how many slips and scanned games they come from. */
export interface OpeningHabitsResult {
  rows: OpeningHabit[];
  /** Positions slipped in twice or more, before the top cut. */
  habits: number;
  /** Scanned games of the period. */
  scanned: number;
}

interface Group {
  fen: Fen;
  colour: Color;
  opening?: string;
  line: San[];
  best: Uci;
  latest: number;
  games: HabitGame[];
}

export class OpeningHabitsAggregator implements Aggregator<OpeningHabitsResult> {
  readonly id = aggregatorId('opening-habits');

  start(): Accumulator<OpeningHabitsResult> {
    const groups = new Map<string, Group>();
    let scanned = 0;
    return {
      add: (g) => {
        if (!g.scanned) return;
        scanned += 1;
        const seen = new Set<string>();
        for (const slip of g.openingSlips ?? []) {
          const key = positionKey(slip.fen);
          // One game counts once per position: its first slip there.
          if (seen.has(key)) continue;
          seen.add(key);
          let group = groups.get(key);
          if (!group) groups.set(key, (group = { fen: slip.fen, colour: g.colour, line: slip.line, best: slip.best, latest: -Infinity, games: [] }));
          if (g.playedAt > group.latest) {
            group.latest = g.playedAt;
            group.opening = g.variation ?? g.opening;
            group.line = slip.line;
            group.best = slip.best;
          }
          group.games.push({ id: g.id, playedAt: g.playedAt, ply: slip.ply, san: slip.san, loss: slip.before - slip.after, reviewPath: g.reviewPath });
        }
      },
      result: () => {
        const habits = [...groups.values()].filter((h) => h.games.length >= HABIT_GAMES).map(toHabit);
        habits.sort((a, b) => b.games.length - a.games.length || b.loss - a.loss || a.fen.localeCompare(b.fen));
        return { rows: habits.slice(0, TOP_HABITS), habits: habits.length, scanned };
      },
    };
  }
}

function toHabit(group: Group): OpeningHabit {
  const games = [...group.games].sort((a, b) => b.playedAt - a.playedAt);
  const counts = new Map<San, number>();
  for (const g of games) counts.set(g.san, (counts.get(g.san) ?? 0) + 1);
  // The commonest move, the newest game's on a tie (the map keeps first insertion, newest first).
  let played = games[0].san;
  for (const [san, n] of counts) if (n > (counts.get(played) ?? 0)) played = san;
  return {
    fen: group.fen,
    colour: group.colour,
    opening: group.opening,
    line: group.line,
    best: group.best,
    played,
    playedGames: counts.get(played) ?? 0,
    games,
    loss: games.reduce((sum, g) => sum + g.loss, 0) / games.length,
  };
}
